import { configureStore, combineReducers } from '@reduxjs/toolkit';

import apiaApiSlice from './apiaApiSlice';
import appStateSlice from './appStateSlice';
import buttonsSlice from './buttonsSlice';
import checkWebDavDocsLocksSlice from './checkWebDavDocsLocksSlice';
import checkWizardSlice from './checkWizardSlice';
import confirmFormSlice from './confirmFormSlice';
import confirmTaskPathSlice from './confirmTaskPathSlice';
import dataGridSlice from './dataGridSlice/dataGridSlice';
import documentsSlice from './documentsSlice';
import documentsMonitorSlice from './documentsMonitorSlice';
import fieldEventsSlice from './fieldEventsSlice';
import fileUploadSlice from './fileUploadSlice';
import querySlice from './querySlice';
import ganttSlice from './ganttSlice';
import globalSlice from './globalSlice';
import hasSignableFormsSlice from './hasSignableFormsSlice';
import informationSlice from './informationSlice';
import JSApiSlice from './JSApiSlice';
import remoteModalsSlice from './remoteModalsSlice';
import testSlice from './testSlice';
import valuesSlice from './valuesSlice';
import previousValidationSlice from './previousValidationSlice';
import responsiveTableSlice from './responsiveTableSlice';
import translationSlice from './translationSlice';
import schedulerSlice from './schedulerSlice';
import profileSlice from './profileSlice';
import listSlice from './listSlice';

const reducer = combineReducers({
  apiaApiSlice,
  appStateSlice,
  buttonsSlice,
  checkWebDavDocsLocksSlice,
  checkWizardSlice,
  confirmFormSlice,
  confirmTaskPathSlice,
  dataGridSlice,
  documentsSlice,
  documentsMonitorSlice,
  fieldEventsSlice,
  fileUploadSlice,
  ganttSlice,
  globalSlice,
  hasSignableFormsSlice,
  informationSlice,
  JSApiSlice,
  listSlice,
  previousValidationSlice,
  profileSlice,
  querySlice,
  remoteModalsSlice,
  responsiveTableSlice,
  schedulerSlice,
  testSlice,
  translationSlice,
  valuesSlice,
});
/*
const logger = createLogger({
  collapsed: true,
  duration: true,
  diff: true,
}); */

const store = configureStore({
  reducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: false,
});

/**
 * Crea un listener del store que trabaja de la misma forma que el hook
 * useAppSelector, pero es perfectamente funcional fuera de un componente React.
 *
 * @param selector Es el mismo tipo de selector que se le pasaría a useAppSelector.
 * @param onUpdate Es un callback que recibe el nuevo estado cada vez que el estado se actualiza.
 * @param eqFunction Es un callback que recibe el estado anterior y el nuevo para determinar si se debe llamar a onUpdate o no.
 * @see shallowEqual
 */
export function makeStoreListener<TSelected>(
  selector: (state: ReturnType<(typeof store)['getState']>) => TSelected,
  onUpdate: (newValue: TSelected) => unknown,
  eqFunction: (left: TSelected, right: TSelected) => boolean,
) {
  let lastValue: TSelected;
  store.subscribe(() => {
    const newValue = selector(store.getState());
    if (
      (eqFunction && eqFunction(lastValue, newValue)) ||
      (!eqFunction && lastValue !== newValue)
    ) {
      lastValue = newValue;
      onUpdate(newValue);
    }
  });
}
declare global {
  interface Window {
    makeStoreListener: typeof makeStoreListener;
  }
}
window.makeStoreListener = makeStoreListener;

// Infer the `RootState` and `AppDispatch` types from the store itself
export type TRootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users:
// UsersState}
export type TAppDispatch = typeof store.dispatch;
export default store;
